import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, Lock, User, Phone, Loader2 } from "lucide-react";

// Locofast Logo Component
const LocoFastLogo = ({ className = "" }) => (
  <svg viewBox="0 0 200 50" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* L icon mark */}
    <rect x="0" y="5" width="40" height="40" rx="8" fill="#2563EB"/>
    <path d="M12 15 L12 35 L28 35" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Text */}
    <text x="50" y="35" fontFamily="Manrope, sans-serif" fontWeight="700" fontSize="28" fill="#1E293B">
      <tspan fill="#2563EB">Loco</tspan><tspan fill="#1E293B">fast</tspan>
    </text>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
  });

  // Get the intended destination from location state or use default
  const getRedirectPath = () => {
    const from = location.state?.from?.pathname;
    if (from && from !== '/login') {
      // If user was trying to access an export route, update localStorage
      if (from.startsWith('/export')) {
        localStorage.setItem('crm_type', 'export');
      }
      return from;
    }
    // Default: check saved CRM preference
    const savedCRM = localStorage.getItem('crm_type');
    return savedCRM === 'export' ? '/export/dashboard' : '/select-crm';
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password,
      }, { timeout: 30000 });

      login(response.data.access_token, response.data.user);
      toast.success("Welcome back!");
      navigate(getRedirectPath());
    } catch (error) {
      console.error("Login failed:", error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      let msg;
      if (detail === "Invalid credentials" || status === 401) {
        msg = "Invalid email or password";
      } else if (typeof detail === "string" && detail) {
        msg = detail;
      } else if (error.code === "ECONNABORTED" || error.message?.toLowerCase().includes("timeout")) {
        msg = "Login timed out. Server is taking too long — please retry in a moment.";
      } else if (error.message === "Network Error") {
        msg = "Cannot reach server. Check your internet and retry.";
      } else if (status === 520 || status === 502 || status === 503 || status === 504) {
        msg = `Server is currently unavailable (HTTP ${status}). Please try again in a minute.`;
      } else if (status) {
        msg = `Login failed (HTTP ${status}). Please retry — if it persists, contact admin.`;
      } else {
        msg = "Login failed. Please retry.";
      }
      toast.error(msg, { duration: 7000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupAdmin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/setup-admin`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        role: "admin",
      });

      login(response.data.access_token, {
        id: response.data.user_id,
        email: formData.email,
        name: formData.name,
        role: "admin",
      });
      
      toast.success("Admin account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Setup failed";
      if (message === "Admin already exists") {
        toast.error("Admin account already exists. Please login.");
        setIsSetupMode(false);
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Brand Section */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 flex-col justify-center items-center p-12 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>
        
        <div className="relative z-10 max-w-md text-center">
          {/* Logo */}
          <div className="mb-8">
            <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto shadow-xl">
              <svg viewBox="0 0 40 40" className="w-12 h-12" fill="none">
                <path d="M8 10 L8 30 L24 30" stroke="#2563EB" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Locofast CRM
          </h1>
          <p className="text-lg text-blue-100 mb-8">
            India's most intelligent B2B fabric sourcing CRM. Manage leads, track calls, and close deals faster.
          </p>
          
          {/* Features */}
          <div className="grid grid-cols-2 gap-4 text-sm text-blue-100">
            <div className="bg-white/10 backdrop-blur rounded-lg p-3">
              <Phone className="w-5 h-5 mx-auto mb-2" />
              <span>Click-to-Call</span>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3">
              <User className="w-5 h-5 mx-auto mb-2" />
              <span>Lead Tracking</span>
            </div>
          </div>
          
          {/* Stats */}
          <div className="mt-8 pt-8 border-t border-white/20 flex justify-center gap-8 text-white">
            <div>
              <div className="text-2xl font-bold">500+</div>
              <div className="text-xs text-blue-200">Seller Partners</div>
            </div>
            <div>
              <div className="text-2xl font-bold">10K+</div>
              <div className="text-xs text-blue-200">Fabric Varieties</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <LocoFastLogo className="h-12 mx-auto" />
          </div>
          
          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="space-y-1 text-center pb-6">
              {/* Logo above form */}
              <div className="hidden lg:block mb-4">
                <LocoFastLogo className="h-10 mx-auto" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">
                {isSetupMode ? "Create Admin Account" : "Welcome Back"}
              </CardTitle>
              <CardDescription className="text-slate-500">
                {isSetupMode
                  ? "Set up your admin account to get started"
                  : "Sign in to your CRM account"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={isSetupMode ? handleSetupAdmin : handleLogin} className="space-y-4">
                {isSetupMode && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleChange}
                          className="pl-10 h-11"
                          required
                          data-testid="name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+91 98765 43210"
                          value={formData.phone}
                          onChange={handleChange}
                          className="pl-10 h-11"
                          required
                          data-testid="phone-input"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@locofast.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 h-11"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 h-11"
                      required
                      minLength={6}
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium btn-press"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isSetupMode ? "Creating Account..." : "Signing In..."}
                    </>
                  ) : (
                    isSetupMode ? "Create Admin Account" : "Sign In"
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setIsSetupMode(!isSetupMode)}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  data-testid="toggle-setup-btn"
                >
                  {isSetupMode
                    ? "Already have an account? Sign In"
                    : "First time? Set up Admin Account"}
                </button>
              </div>
            </CardContent>
          </Card>
          
          {/* Footer */}
          <p className="text-center text-xs text-slate-400 mt-6">
            Powered by Locofast - India's Largest B2B Textile Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
